import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useApp } from '../../context';
import { canAccess } from '../../utils/rbac';
import api from '../../hooks/useApi';
import { formatDate } from '../../utils/format';
import { useToast } from '../ui/Toast';
import Modal from '../shared/Modal';
import FileUpload from '../shared/FileUpload';
import FilePreviewModal from '../shared/FilePreviewModal';
import FeatureTour from '../shared/FeatureTour';
import KanbanBoard from '../shared/KanbanBoard';
import useFeatureTour from '../../hooks/useFeatureTour';
import {
  STAGES, STAGE_LABELS, SOURCES, SOURCE_LABELS, VACANCY_STATUS_LABELS,
  REASONS, REASON_LABELS, STALLED_AFTER_DAYS,
  INTERVIEW_TYPES, INTERVIEW_TYPE_LABELS, INTERVIEW_TYPE_STAGE, RECOMMENDATION_LABELS,
  OFFER_STATUS_LABELS, candidateCardTint,
} from '../../utils/talentStages';
import { TALENT_TOUR_ID, talentTourSteps } from './talentAcquisitionTour';

// ─── Talent Acquisition ──────────────────────────────────────────────────────
// The pipeline board is the daily working view; the vacancy workspace is the
// detail view for one role. Both read the same two lists (vacancies and
// candidates) and share the candidate panel. Salary is only rendered when the
// person may see it; the API omits the fields for everyone else anyway, so
// this is a courtesy rather than the control.

const STAGE_BADGE = {
  applied: 'bg-gray-100 text-gray-700',
  screening: 'bg-blue-50 text-blue-700',
  interview_1: 'bg-purple-50 text-purple-700',
  interview_2: 'bg-purple-50 text-purple-700',
  offer: 'bg-amber-50 text-amber-700',
  accepted: 'bg-green-50 text-green-700',
};

const VACANCY_BADGE = {
  pending_approval: 'bg-amber-50 text-amber-700',
  open: 'bg-green-50 text-green-700',
  filled: 'bg-blue-50 text-blue-700',
  closed: 'bg-gray-100 text-gray-600',
  rejected: 'bg-red-50 text-red-700',
};

const inputCls = 'w-full border border-gray-200 rounded px-2 py-1.5 text-xs bg-white text-gray-800';
const labelCls = 'block text-[10px] font-semibold text-gray-500 uppercase mb-0.5';
const btnPrimary = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-navy text-white hover:opacity-90 disabled:opacity-50';
const btnGold = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-gold text-white hover:opacity-90 disabled:opacity-50';
const btnGhost = 'px-3 py-1.5 text-[11px] font-semibold rounded bg-white border border-gray-200 text-navy hover:bg-gray-50 disabled:opacity-50';

const personName = (p) => `${p.first_name || ''} ${p.last_name || ''}`.trim();
const initials = (first, last) => `${(first || '')[0] || ''}${(last || '')[0] || ''}`.toUpperCase();
const money = (v) => (v == null || v === '' ? '' : `£${Number(v).toLocaleString('en-GB')}`);
const stageIndex = (s) => STAGES.indexOf(s);

// The candidate fields the panel edits. The API takes the same set, minus the
// salary for anyone who may not see it.
const BLANK_DETAILS = { first_name: '', last_name: '', email: '', phone: '', source: '', source_detail: '', job_title: '', desired_salary: '' };
const detailsOf = (c) => Object.fromEntries(
  Object.keys(BLANK_DETAILS).map(k => [k, c[k] == null ? '' : c[k]]),
);

function Badge({ cls, children }) {
  return <span className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${cls}`}>{children}</span>;
}

// ─── Raise / edit vacancy ────────────────────────────────────────────────────

const CLEARANCE_LABELS = { sc: 'SC', bpss: 'BPSS', none: 'No clearance' };

function VacancyModal({ vacancy, teams, people, canSeeSalary, onSave, onClose }) {
  const [form, setForm] = useState(() => ({
    title: vacancy?.title || '',
    team_id: vacancy?.team_id || '',
    hiring_manager_id: vacancy?.hiring_manager_id || '',
    headcount: vacancy?.headcount || 1,
    reason: vacancy?.reason || 'growth',
    description: vacancy?.description || '',
    clearance_target: vacancy?.clearance_target || 'sc',
    start_by: vacancy?.start_by || '',
    salary_band_low: vacancy?.salary_band_low ?? '',
    salary_band_high: vacancy?.salary_band_high ?? '',
    day_rate_band_low: vacancy?.day_rate_band_low ?? '',
    day_rate_band_high: vacancy?.day_rate_band_high ?? '',
    channels: vacancy?.channels || [],
    folder_url: vacancy?.folder_url || '',
    notes: vacancy?.notes || '',
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const toggleChannel = (c) => setForm(f => ({ ...f, channels: f.channels.includes(c) ? f.channels.filter(x => x !== c) : [...f.channels, c] }));

  const submit = async () => {
    if (!form.title.trim()) { setError('Give the vacancy a title.'); return; }
    if (canSeeSalary && form.salary_band_low !== '' && form.salary_band_high !== '' && Number(form.salary_band_low) > Number(form.salary_band_high)) {
      setError('The bottom of the band is above the top.'); return;
    }
    if (canSeeSalary && form.day_rate_band_low !== '' && form.day_rate_band_high !== '' && Number(form.day_rate_band_low) > Number(form.day_rate_band_high)) {
      setError('The bottom of the day-rate band is above the top.'); return;
    }
    setSaving(true); setError('');
    try {
      const payload = { ...form, headcount: Number(form.headcount) || 1 };
      if (!canSeeSalary) { for (const k of ['salary_band_low', 'salary_band_high', 'day_rate_band_low', 'day_rate_band_high']) delete payload[k]; }
      await onSave(payload);
    } catch (e) {
      setError(e.message || 'Could not save the vacancy.');
    } finally {
      setSaving(false);
    }
  };

  const managers = people.filter(p => p.employee_status !== 'left');

  return (
    <Modal title={vacancy ? `Edit ${vacancy.reference}` : 'Raise a vacancy'} onClose={onClose} wide
      footer={<>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} onClick={submit} disabled={saving}>{saving ? 'Saving…' : vacancy ? 'Save changes' : 'Send for approval'}</button>
      </>}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="sm:col-span-2">
          <label className={labelCls}>Job title</label>
          <input className={inputCls} value={form.title} onChange={set('title')} placeholder="e.g. Senior AV Engineer" autoFocus />
        </div>
        <div>
          <label className={labelCls}>Team</label>
          <select className={inputCls} value={form.team_id} onChange={set('team_id')}>
            <option value="">Not set</option>
            {teams.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Hiring manager</label>
          <select className={inputCls} value={form.hiring_manager_id} onChange={set('hiring_manager_id')}>
            <option value="">Not set</option>
            {managers.map(p => <option key={p.id} value={p.id}>{personName(p)}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Headcount</label>
          <input type="number" min="1" className={inputCls} value={form.headcount} onChange={set('headcount')} />
        </div>
        <div>
          <label className={labelCls}>Reason</label>
          <select className={inputCls} value={form.reason} onChange={set('reason')}>
            {REASONS.map(r => <option key={r} value={r}>{REASON_LABELS[r]}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Clearance needed</label>
          <select className={inputCls} value={form.clearance_target} onChange={set('clearance_target')}>
            <option value="sc">SC (full clearance)</option>
            <option value="bpss">BPSS only</option>
            <option value="none">None (no clearance needed)</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Fill by</label>
          <input type="date" className={inputCls} value={form.start_by} onChange={set('start_by')} />
        </div>
        {canSeeSalary && (
          <>
            <div>
              <label className={labelCls}>Salary band from (£)</label>
              <input type="number" min="0" step="500" className={inputCls} value={form.salary_band_low} onChange={set('salary_band_low')} />
            </div>
            <div>
              <label className={labelCls}>Salary band to (£)</label>
              <input type="number" min="0" step="500" className={inputCls} value={form.salary_band_high} onChange={set('salary_band_high')} />
            </div>
            <div>
              <label className={labelCls}>Contractor day rate from (£)</label>
              <input type="number" min="0" step="25" className={inputCls} value={form.day_rate_band_low} onChange={set('day_rate_band_low')} />
            </div>
            <div>
              <label className={labelCls}>Contractor day rate to (£)</label>
              <input type="number" min="0" step="25" className={inputCls} value={form.day_rate_band_high} onChange={set('day_rate_band_high')} />
            </div>
          </>
        )}
        <div className="sm:col-span-2">
          <label className={labelCls}>Description</label>
          <textarea rows={3} className={inputCls} value={form.description} onChange={set('description')} placeholder="What the role is for and what a good candidate looks like" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Advertising channels</label>
          <div className="flex flex-wrap gap-3">
            {SOURCES.map(c => (
              <label key={c} className="flex items-center gap-1 text-xs text-gray-700">
                <input type="checkbox" checked={form.channels.includes(c)} onChange={() => toggleChannel(c)} /> {SOURCE_LABELS[c]}
              </label>
            ))}
          </div>
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Folder link (SharePoint)</label>
          <input className={inputCls} value={form.folder_url} onChange={set('folder_url')} placeholder="https://…" />
        </div>
        <div className="sm:col-span-2">
          <label className={labelCls}>Notes</label>
          <textarea rows={2} className={inputCls} value={form.notes} onChange={set('notes')} />
        </div>
      </div>
      {error && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
      {!vacancy && <div className="mt-2 text-[10px] text-gray-500">The vacancy goes to the approver first. Candidates can be added once it is approved.</div>}
    </Modal>
  );
}

// ─── Add candidate ───────────────────────────────────────────────────────────

function CandidateModal({ vacancies, defaultVacancyId, canSeeSalary = false, onSave, onClose }) {
  const openVacancies = vacancies.filter(v => v.status === 'open');
  const [form, setForm] = useState({
    vacancy_id: defaultVacancyId && openVacancies.some(v => v.id === defaultVacancyId) ? defaultVacancyId : (openVacancies[0]?.id || ''),
    first_name: '', last_name: '', email: '', phone: '',
    source: 'linkedin', source_detail: '', consent_future_roles: false, note: '',
    cv_url: '', cv_filename: '', job_title: '', desired_salary: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // Reading the CV. Once the file is uploaded the server reads it and offers
  // first name, last name, email, phone and job title. Only boxes still empty
  // are filled, each is marked "from CV" until the person edits it, and a
  // failure is said under the CV control; the form saves regardless.
  const [cvRead, setCvRead] = useState({ status: 'idle', message: '' }); // idle | reading | done | error
  const [fromCv, setFromCv] = useState(new Set());
  const formRef = useRef(form);
  formRef.current = form;
  const set = (k) => (e) => {
    setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
    if (fromCv.has(k)) setFromCv(s => { const n = new Set(s); n.delete(k); return n; });
  };

  const readCv = async (cvUrl) => {
    setCvRead({ status: 'reading', message: '' });
    try {
      const { fields } = await api.post('talent/candidates/extract-cv', { cv_url: cvUrl });
      // Decided against the form as it is now, after the wait, so anything
      // typed while the CV was being read is kept.
      const current = formRef.current;
      const filled = ['first_name', 'last_name', 'email', 'phone', 'job_title']
        .filter(k => !String(current[k] || '').trim() && fields?.[k]);
      if (filled.length) setForm(f => ({ ...f, ...Object.fromEntries(filled.map(k => [k, fields[k]])) }));
      setFromCv(new Set(filled));
      setCvRead({ status: 'done', message: filled.length ? 'Details taken from the CV are marked; check them before adding.' : 'Nothing new was found in the CV to fill in.' });
    } catch (e) {
      setCvRead({ status: 'error', message: e.data?.message || 'Could not read the CV. Fill in the details by hand.' });
    }
  };
  const cvMark = (k) => fromCv.has(k) ? <span className="ml-1 text-[9px] font-normal text-gold">from CV</span> : null;

  const submit = async () => {
    if (!form.vacancy_id) { setError('Choose the vacancy.'); return; }
    if (!form.first_name.trim() || !form.last_name.trim()) { setError('Enter the candidate\'s first and last name.'); return; }
    setSaving(true); setError('');
    const payload = { ...form };
    if (!canSeeSalary) delete payload.desired_salary;
    try { await onSave(payload); } catch (e) { setError(e.message || 'Could not add the candidate.'); } finally { setSaving(false); }
  };

  return (
    <Modal title="Add a candidate" onClose={onClose} wide
      footer={<>
        <button className={btnGhost} onClick={onClose}>Cancel</button>
        <button className={btnPrimary} onClick={submit} disabled={saving || !openVacancies.length}>{saving ? 'Adding…' : 'Add candidate'}</button>
      </>}>
      {!openVacancies.length ? (
        <div className="text-xs text-gray-600 bg-amber-50 border border-amber-200 rounded px-3 py-2">
          No vacancy is open yet. Raise one and have it approved, then add candidates to it.
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Vacancy</label>
            <select className={inputCls} value={form.vacancy_id} onChange={set('vacancy_id')}>
              {openVacancies.map(v => <option key={v.id} value={v.id}>{v.reference} · {v.title}</option>)}
            </select>
          </div>
          <div><label className={labelCls}>First name{cvMark('first_name')}</label><input className={inputCls} value={form.first_name} onChange={set('first_name')} autoFocus /></div>
          <div><label className={labelCls}>Last name{cvMark('last_name')}</label><input className={inputCls} value={form.last_name} onChange={set('last_name')} /></div>
          <div><label className={labelCls}>Email{cvMark('email')}</label><input type="email" className={inputCls} value={form.email} onChange={set('email')} /></div>
          <div><label className={labelCls}>Phone{cvMark('phone')}</label><input className={inputCls} value={form.phone} onChange={set('phone')} /></div>
          <div className={canSeeSalary ? '' : 'sm:col-span-2'}>
            <label className={labelCls}>Job title{cvMark('job_title')}</label>
            <input className={inputCls} value={form.job_title} onChange={set('job_title')} placeholder="Current or applied-for title" />
          </div>
          {canSeeSalary && (
            <div>
              <label className={labelCls}>Desired salary (£ per year)</label>
              <input type="number" min="0" step="500" className={inputCls} value={form.desired_salary} onChange={set('desired_salary')} />
            </div>
          )}
          <div>
            <label className={labelCls}>Source</label>
            <select className={inputCls} value={form.source} onChange={set('source')}>
              {SOURCES.map(s => <option key={s} value={s}>{SOURCE_LABELS[s]}</option>)}
            </select>
          </div>
          <div>
            <label className={labelCls}>{form.source === 'referral' ? 'Referred by' : form.source === 'job_board' ? 'Which board' : 'Source detail'}</label>
            <input className={inputCls} value={form.source_detail} onChange={set('source_detail')} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>CV</label>
            <FileUpload accept=".pdf,.doc,.docx" label="Drag the CV here or click to browse. PDF and .docx CVs fill in the details above."
              onUpload={(r) => { setForm(f => ({ ...f, cv_url: r.url, cv_filename: r.filename })); readCv(r.url); }}
              onClear={() => { setForm(f => ({ ...f, cv_url: '', cv_filename: '' })); setCvRead({ status: 'idle', message: '' }); }} />
            {cvRead.status === 'reading' && <div className="mt-1 text-[10px] text-gray-500 flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 border-2 border-gray-300 border-t-gold rounded-full animate-spin" />Reading the CV…</div>}
            {cvRead.status === 'done' && <div className="mt-1 text-[10px] text-gray-500">{cvRead.message}</div>}
            {cvRead.status === 'error' && <div className="mt-1 text-[10px] text-amber-700">{cvRead.message}</div>}
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Note</label>
            <input className={inputCls} value={form.note} onChange={set('note')} placeholder="Anything worth recording at the point of application" />
          </div>
          <div className="sm:col-span-2">
            <label className="flex items-start gap-2 text-xs text-gray-700">
              <input type="checkbox" className="mt-0.5" checked={form.consent_future_roles} onChange={set('consent_future_roles')} />
              <span>The candidate has agreed to be kept on file for future roles. <span className="text-gray-400">Without this, unsuccessful candidates are removed six months after the vacancy closes.</span></span>
            </label>
          </div>
        </div>
      )}
      {error && <div className="mt-2 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
    </Modal>
  );
}

// ─── Book an interview ───────────────────────────────────────────────────────
// Inline in the candidate panel. The type chooses a stage, and by default the
// candidate moves there when the interview is booked; untick to leave them.

const REC_BADGE = { proceed: 'bg-green-50 text-green-700', hold: 'bg-amber-50 text-amber-700', reject: 'bg-red-50 text-red-700' };

function localToIso(local) {
  if (!local) return '';
  const d = new Date(local);            // datetime-local is the browser's local time
  return isNaN(d) ? '' : d.toISOString();
}

function fmtWhen(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return `${d.toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' })} ${d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}`;
}

function BookInterviewForm({ candidate, people, onBooked, onCancel }) {
  const defaultType = candidate.stage === 'applied' ? 'screening' : candidate.stage === 'screening' ? 'interview_1' : candidate.stage === 'interview_1' ? 'interview_2' : 'interview_1';
  const [form, setForm] = useState({ type: defaultType, when: '', duration_minutes: 60, location: 'Microsoft Teams', notes: '', panel: [], move: true });
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.type === 'checkbox' ? e.target.checked : e.target.value }));
  const togglePanel = (id) => setForm(f => ({ ...f, panel: f.panel.includes(id) ? f.panel.filter(x => x !== id) : [...f.panel, id] }));

  const stage = INTERVIEW_TYPE_STAGE[form.type];
  const moveOffered = stage && stage !== candidate.stage;
  const q = search.trim().toLowerCase();
  const pool = people
    .filter(p => p.employee_status !== 'left')
    .filter(p => !q || personName(p).toLowerCase().includes(q))
    .slice(0, q ? 12 : 8);
  const chosen = people.filter(p => form.panel.includes(p.id));

  const submit = async () => {
    if (!form.when) { setError('Set the date and time.'); return; }
    if (!form.panel.length) { setError('Add at least one interviewer to the panel.'); return; }
    setSaving(true); setError('');
    try {
      await onBooked({
        candidate_id: candidate.id, type: form.type, scheduled_at: localToIso(form.when),
        duration_minutes: Number(form.duration_minutes) || 60, location: form.location, notes: form.notes,
        panel: form.panel, move_to_stage: moveOffered && form.move ? stage : undefined,
      });
    } catch (e) {
      setError(e.message || 'Could not book the interview.');
    } finally { setSaving(false); }
  };

  return (
    <div className="border border-gray-200 rounded p-2 space-y-2 bg-gray-50" data-tour="book-interview-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <label className={labelCls}>Type</label>
          <select className={inputCls} value={form.type} onChange={set('type')}>
            {INTERVIEW_TYPES.map(t => <option key={t} value={t}>{INTERVIEW_TYPE_LABELS[t]}</option>)}
          </select>
        </div>
        <div>
          <label className={labelCls}>Length (minutes)</label>
          <input type="number" min="15" step="15" className={inputCls} value={form.duration_minutes} onChange={set('duration_minutes')} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Date and time</label>
          <input type="datetime-local" className={inputCls} value={form.when} onChange={set('when')} />
        </div>
        <div className="col-span-2">
          <label className={labelCls}>Where</label>
          <input className={inputCls} value={form.location} onChange={set('location')} placeholder="Microsoft Teams, Derby office, phone" />
        </div>
      </div>
      <div>
        <label className={labelCls}>Panel</label>
        {chosen.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-1">
            {chosen.map(p => (
              <button key={p.id} type="button" onClick={() => togglePanel(p.id)} className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-navy text-white" title="Remove from panel">{personName(p)} ×</button>
            ))}
          </div>
        )}
        <input className={inputCls} value={search} onChange={e => setSearch(e.target.value)} placeholder="Search people to add" />
        <div className="max-h-28 overflow-y-auto border border-gray-200 rounded mt-1 bg-white">
          {pool.filter(p => !form.panel.includes(p.id)).map(p => (
            <button key={p.id} type="button" onClick={() => { togglePanel(p.id); setSearch(''); }}
              className="w-full text-left px-2 py-1 text-[11px] text-gray-700 hover:bg-amber-50 border-b border-gray-100 last:border-0">
              {personName(p)}{p.job_title ? <span className="text-gray-400"> · {p.job_title}</span> : null}
            </button>
          ))}
          {pool.filter(p => !form.panel.includes(p.id)).length === 0 && <div className="px-2 py-1 text-[11px] text-gray-400">No matches</div>}
        </div>
      </div>
      <div>
        <label className={labelCls}>Brief for the panel</label>
        <textarea rows={2} className={inputCls} value={form.notes} onChange={set('notes')} placeholder="What to probe, who covers what. The candidate does not see this." />
      </div>
      {moveOffered && (
        <label className="flex items-center gap-2 text-[11px] text-gray-700">
          <input type="checkbox" checked={form.move} onChange={set('move')} /> Move {candidate.first_name} to {STAGE_LABELS[stage]} now
        </label>
      )}
      <div className="text-[10px] text-gray-500">Invites go to the panel and to the candidate{candidate.email ? '' : ' (no email on file, so only the panel this time)'}. Each interviewer gets an Action due the next working day.</div>
      {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}
      <div className="flex gap-1">
        <button className={btnPrimary} disabled={saving} onClick={submit}>{saving ? 'Booking…' : 'Book and send invites'}</button>
        <button className={btnGhost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ─── Offers ──────────────────────────────────────────────────────────────────
// Inline in the candidate panel. Holders of talent-acquisition:manage (HR and
// admin by default) make offers, and holders of the salary key see the figures
// (the API strips salary for everyone else); a hiring manager sees the status. Within band an offer is approved on creation; above band it waits
// for the approver on the Hiring tab of Approvals. Accepted is the hand-off: ops creates
// the person and the onboarding record, and offers to let the others know.

const OFFER_BADGE = {
  pending_approval: 'bg-amber-50 text-amber-700',
  approved: 'bg-blue-50 text-blue-700',
  rejected: 'bg-red-50 text-red-700',
  sent: 'bg-purple-50 text-purple-700',
  accepted: 'bg-green-50 text-green-700',
  declined: 'bg-red-50 text-red-700',
  withdrawn: 'bg-gray-100 text-gray-600',
};

function OfferSection({ candidate, canSeeSalary, canManage, navigate, act, busy }) {
  const [making, setMaking] = useState(false);
  const [form, setForm] = useState({ pay_basis: 'salary', contract_type: 'permanent', salary: '', day_rate: '', ir35: '', start_date: '', end_date: '', clearance_target: candidate.clearance_target || 'sc', conditions: 'Subject to satisfactory references and BPSS clearance', notes: '' });
  const [declining, setDeclining] = useState(null);   // offer id with the reason box open
  const [reason, setReason] = useState('');
  const [handoff, setHandoff] = useState(null);       // result of an acceptance, for the regret prompt
  const [regretting, setRegretting] = useState(false);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const offers = candidate.offers || [];
  const live = offers.find(o => ['pending_approval', 'approved', 'sent'].includes(o.status));
  const accepted = offers.find(o => o.status === 'accepted');
  const active = candidate.status === 'active';
  const bands = candidate.vacancy_band;   // { low, high, dayLow, dayHigh } when the page knows it
  // A contractor offer is a day rate and IR35 status in place of salary, held
  // against the vacancy's day-rate band rather than its salary band.
  // A salaried offer is permanent or fixed-term; a fixed-term one has an end
  // date, which becomes the person's End of Employment on acceptance.
  const contractor = form.pay_basis === 'contractor';
  const fixedTerm = !contractor && form.contract_type === 'fixed_term';
  const kind = contractor ? 'contractor' : form.contract_type;
  const figure = contractor ? form.day_rate : form.salary;
  const band = bands && (contractor ? { low: bands.dayLow, high: bands.dayHigh } : { low: bands.low, high: bands.high });
  const aboveBand = figure && band?.high != null && Number(figure) > Number(band.high);

  const make = () => {
    if (!figure || Number(figure) <= 0) { return Promise.reject(new Error(contractor ? 'Enter the day rate.' : 'Enter the salary.')); }
    if (contractor && !form.ir35) { return Promise.reject(new Error('Say whether the contract is inside or outside IR35.')); }
    if (!form.start_date) { return Promise.reject(new Error('Set the proposed start date.')); }
    if (fixedTerm && !form.end_date) { return Promise.reject(new Error('Set the date the fixed-term contract ends.')); }
    if (fixedTerm && form.end_date <= form.start_date) { return Promise.reject(new Error('The contract has to end after it starts.')); }
    const payload = contractor
      ? { ...form, salary: undefined, day_rate: Number(form.day_rate), contract_type: undefined, end_date: undefined }
      : { ...form, salary: Number(form.salary), day_rate: undefined, ir35: undefined, end_date: fixedTerm ? form.end_date : undefined };
    return act(() => api.post('talent/offers', { ...payload, candidate_id: candidate.id }).then(() => { setMaking(false); return null; }),
      aboveBand ? 'Offer made; it is above the band so it waits for approval' : 'Offer made and approved (within band)');
  };
  const [makeError, setMakeError] = useState('');
  const submitMake = () => make().catch(e => setMakeError(e.message));

  const markSent = (o) => act(
    () => api.post(`talent/offers/${o.id}/sent`, {}).then(() => null),
    candidate.email ? `Offer emailed to ${candidate.email}` : 'Offer recorded as sent (no email on file, so send the letter by hand)',
  );
  const withdraw = (o) => { if (window.confirm('Withdraw this offer? The candidate stays active and a new offer can follow.')) act(() => api.post(`talent/offers/${o.id}/withdraw`, {}).then(() => null), 'Offer withdrawn'); };
  const decline = (o) => act(() => api.post(`talent/offers/${o.id}/outcome`, { outcome: 'declined', reason }).then(() => { setDeclining(null); setReason(''); return null; }), 'Recorded: offer declined');
  const accept = (o) => {
    if (!window.confirm(`${candidate.first_name} ${candidate.last_name} has accepted?\n\nThis creates their person record and an onboarding record with Offer & Contract complete, and fills the vacancy when the headcount is reached.`)) return;
    act(async () => {
      const r = await api.post(`talent/offers/${o.id}/outcome`, { outcome: 'accepted' });
      setHandoff(r);
      return null;
    }, 'Offer accepted. Person and onboarding record created');
  };
  const regretOthers = () => act(async () => {
    setRegretting(true);
    for (const c of handoff.remaining_candidates) {
      await api.post(`talent/candidates/${c.id}/reject`, { reason: 'Position filled', notify: true });
    }
    setHandoff(h => ({ ...h, remaining_candidates: [], remaining_active: 0, regretted: true }));
    setRegretting(false);
    return null;
  }, 'Remaining candidates marked as unsuccessful and emailed where we have an address');

  const money = (v) => (v == null ? '' : `£${Number(v).toLocaleString('en-GB')}`);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] font-bold text-gray-500 uppercase">Offer</div>
        {canManage && active && !live && !accepted && !making && (
          <button data-tour="make-offer" className="text-[10px] text-gold hover:underline" onClick={() => setMaking(true)}>Make offer</button>
        )}
      </div>

      {making && (
        <div className="border border-gray-200 rounded p-2 space-y-2 bg-gray-50 mb-2">
          <div className="inline-flex rounded border border-gray-200 overflow-hidden text-[11px]" role="group" aria-label="Kind of offer">
            {[['permanent', 'Permanent'], ['fixed_term', 'Fixed term'], ['contractor', 'Contractor']].map(([k, label]) => (
              <button key={k} type="button" aria-pressed={kind === k}
                className={`px-3 py-1 font-semibold ${kind === k ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                onClick={() => {
                  setForm(f => (k === 'contractor' ? { ...f, pay_basis: 'contractor' } : { ...f, pay_basis: 'salary', contract_type: k }));
                  setMakeError('');
                }}>{label}</button>
            ))}
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              {contractor ? (<>
                <label className={labelCls}>Day rate (£ a day)</label>
                <input type="number" min="0" step="25" className={inputCls} value={form.day_rate} onChange={set('day_rate')} autoFocus />
              </>) : (<>
                <label className={labelCls}>Salary (£ a year)</label>
                <input type="number" min="0" step="500" className={inputCls} value={form.salary} onChange={set('salary')} autoFocus />
              </>)}
              {band && (band.low != null || band.high != null) && (
                <div className="text-[10px] text-gray-500 mt-0.5">Band {money(band.low)} to {money(band.high)}{contractor ? ' a day' : ''}{aboveBand ? <span className="text-amber-700 font-semibold"> · above band, needs approval</span> : ''}</div>
              )}
            </div>
            {contractor && (
              <div>
                <label className={labelCls}>IR35</label>
                <select className={inputCls} value={form.ir35} onChange={set('ir35')}>
                  <option value="">Choose</option>
                  <option value="outside">Outside IR35</option>
                  <option value="inside">Inside IR35</option>
                </select>
              </div>
            )}
            <div>
              <label className={labelCls}>Proposed start</label>
              <input type="date" className={inputCls} value={form.start_date} onChange={set('start_date')} />
            </div>
            {fixedTerm && (
              <div>
                <label className={labelCls}>Contract ends</label>
                <input type="date" className={inputCls} value={form.end_date} min={form.start_date || undefined} onChange={set('end_date')} />
              </div>
            )}
            <div>
              <label className={labelCls}>Clearance</label>
              <select className={inputCls} value={form.clearance_target} onChange={set('clearance_target')}>
                <option value="sc">SC (full clearance)</option>
                <option value="bpss">BPSS only</option>
                <option value="none">None (no clearance needed)</option>
              </select>
            </div>
            <div>
              <label className={labelCls}>Conditions</label>
              <input className={inputCls} value={form.conditions} onChange={set('conditions')} />
            </div>
            <div className="col-span-2">
              <label className={labelCls}>Notes</label>
              <input className={inputCls} value={form.notes} onChange={set('notes')} placeholder="Anything the approver or the file should know" />
            </div>
          </div>
          {makeError && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{makeError}</div>}
          <div className="flex gap-1">
            <button className={btnPrimary} disabled={busy} onClick={submitMake}>Make offer</button>
            <button className={btnGhost} onClick={() => { setMaking(false); setMakeError(''); }}>Cancel</button>
          </div>
        </div>
      )}

      {handoff && (
        <div className="border border-green-200 bg-green-50 rounded p-2 mb-2 text-[11px] text-gray-700 space-y-1">
          <div className="font-semibold text-green-700">Handed to Onboarding.</div>
          <div>The person record and onboarding record exist, with Offer &amp; Contract already complete.{handoff.vacancy_filled ? ' The vacancy is now filled.' : ''}</div>
          <div><button className="text-gold hover:underline" onClick={() => navigate('onboarding')}>Open Onboarding</button></div>
          {handoff.remaining_active > 0 && !handoff.regretted && (
            <div className="mt-1 border-t border-green-200 pt-1">
              {handoff.remaining_active} other candidate{handoff.remaining_active === 1 ? ' is' : 's are'} still active on this vacancy
              {handoff.vacancy_filled ? ', which is now filled' : ''}: {handoff.remaining_candidates.map(c => `${c.first_name} ${c.last_name}`).join(', ')}.
              <div className="mt-1 flex gap-1">
                <button className={btnGhost} disabled={busy || regretting} onClick={regretOthers}>Mark them unsuccessful and send the regret email</button>
                <button className="text-[10px] text-gray-500 hover:underline" onClick={() => setHandoff(h => ({ ...h, regretted: true }))}>Leave them for now</button>
              </div>
            </div>
          )}
        </div>
      )}

      {offers.length === 0 && !making && <div className="text-[11px] text-gray-400">No offer yet.</div>}
      <ul className="space-y-1.5">
        {offers.map(o => (
          <li key={o.id} className="border border-gray-200 rounded px-2 py-1.5 text-[11px]">
            <div className="flex items-start justify-between gap-2">
              <div>
                <Badge cls={OFFER_BADGE[o.status]}>{OFFER_STATUS_LABELS[o.status] || o.status}</Badge>
                {canSeeSalary && o.salary != null && <span className="ml-1 font-semibold text-navy">{money(o.salary)}</span>}
                {canSeeSalary && o.day_rate != null && <span className="ml-1 font-semibold text-navy">{money(o.day_rate)} a day</span>}
                {o.pay_basis === 'contractor' && <span className="text-gray-500"> · contractor, {o.ir35 === 'inside' ? 'inside' : 'outside'} IR35</span>}
                {o.contract_type === 'fixed_term' && <span className="text-gray-500"> · fixed term to {formatDate(o.end_date)}</span>}
                <span className="text-gray-500"> · start {formatDate(o.start_date)} · {CLEARANCE_LABELS[o.clearance_target] || 'SC'}</span>
                {o.above_band ? <span className="text-amber-700"> · above band</span> : null}
                {o.conditions && <div className="text-gray-500">{o.conditions}</div>}
                {o.status === 'pending_approval' && <div className="text-gray-500">Waiting for an offer approver on Approvals, Hiring tab.</div>}
                {o.approval_note && o.status !== 'pending_approval' && <div className="text-gray-500">{o.approval_note}{o.approver_name ? ` (${o.approver_name})` : ''}</div>}
                {o.rejection_reason && <div className="text-red-700">Refused: {o.rejection_reason}</div>}
                {o.decline_reason && <div className="text-gray-600">{o.status === 'withdrawn' ? 'Withdrawn' : 'Declined'}: {o.decline_reason}</div>}
                {o.status === 'accepted' && candidate.onboarding_record_id && (
                  <div><button className="text-gold hover:underline" onClick={() => navigate('onboarding')}>Open Onboarding</button></div>
                )}
              </div>
              {canManage && (
                <div className="flex flex-col gap-1 items-end">
                  {o.status === 'approved' && (
                    <button className={btnPrimary} disabled={busy} onClick={() => markSent(o)}
                      title={candidate.email ? 'Emails the offer to the candidate and records it as sent' : 'No email on file: records the offer as sent'}>
                      {candidate.email ? 'Send offer' : 'Mark as sent'}
                    </button>
                  )}
                  {o.status === 'sent' && <button className={btnPrimary} disabled={busy} onClick={() => accept(o)}>Accepted</button>}
                  {o.status === 'sent' && <button className={`${btnGhost} text-red-700`} disabled={busy} onClick={() => { setDeclining(declining === o.id ? null : o.id); setReason(''); }}>Declined</button>}
                  {['pending_approval', 'approved', 'sent'].includes(o.status) && <button className="text-[10px] text-gray-400 hover:text-red-600" disabled={busy} onClick={() => withdraw(o)}>Withdraw</button>}
                </div>
              )}
            </div>
            {declining === o.id && (
              <div className="mt-1 flex gap-1">
                <input className={inputCls} value={reason} onChange={e => setReason(e.target.value)} placeholder="Why they declined (required)" autoFocus />
                <button className="px-3 py-1.5 text-[11px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" disabled={busy || !reason.trim()} onClick={() => decline(o)}>Confirm</button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

// ─── Erase a candidate ───────────────────────────────────────────────────────
// A GDPR erasure: permanent, where Reject keeps the record. Offered only to
// holders of talent-acquisition:delete, the key the API asks. A failure is
// reported here beside the buttons; when there is one, the record is unchanged.

function DeleteCandidateModal({ candidate, onClose, onDeleted }) {
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const name = `${candidate.first_name || ''} ${candidate.last_name || ''}`.trim() || 'this candidate';
  const confirmed = typed.trim().toUpperCase() === 'DELETE';

  const erase = async () => {
    if (!confirmed) { setError('Type DELETE to confirm.'); return; }
    setBusy(true); setError('');
    try {
      await api.delete(`talent/candidates/${candidate.id}`);
      onDeleted(name);
    } catch (e) {
      setError(e.message || 'Could not erase the candidate. Nothing has changed.');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Delete ${name} permanently?`} onClose={onClose}
      footer={<>
        {error && <span className="mr-auto self-center text-[10px] font-semibold text-red-700">{error}</span>}
        <button className={btnGhost} disabled={busy} onClick={onClose}>Keep candidate</button>
        <button className="px-3 py-1.5 text-[11px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
          disabled={busy || !confirmed} onClick={erase}>{busy ? 'Deleting…' : 'Delete permanently'}</button>
      </>}>
      <div className="space-y-3 text-[11px]">
        <p className="text-gray-700">
          This permanently erases <b className="text-navy">{name}</b>{candidate.vacancy_reference ? ` (${candidate.vacancy_reference})` : ''} and
          everything held about them: interviews and panel feedback, the interviewers' feedback Actions, offers, documents
          and the CV (from storage as well), notes and their history. It cannot be undone.
        </p>
        <p className="text-gray-500">
          Use it for a GDPR erasure request. The audit trail keeps what was done and by whom, with their name and details
          removed, and records that a candidate was erased. To take somebody off the board and keep the record, use Reject instead.
        </p>
        <div>
          <label className={labelCls}>Type DELETE to confirm</label>
          <input className={inputCls} value={typed} placeholder="DELETE" autoFocus
            onChange={e => { setTyped(e.target.value); if (error) setError(''); }} />
        </div>
      </div>
    </Modal>
  );
}

// ─── Candidate panel ─────────────────────────────────────────────────────────
// Slides in from the right. Everything that changes a candidate happens here,
// next to the candidate, and the result shows here too.

function CandidatePanel({ candidateId, people = [], canSeeSalary = false, canManage = false, canDelete = false, navigate, vacancyBand = null, onClose, onChanged }) {
  const { toast } = useToast();
  const [c, setC] = useState(null);
  const [busy, setBusy] = useState(false);
  const [erasing, setErasing] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');
  const [notifyOnReject, setNotifyOnReject] = useState(true);   // send the generic regret email
  const [moveNote, setMoveNote] = useState('');
  const [editingNotes, setEditingNotes] = useState(false);
  const [notes, setNotes] = useState('');
  const [editingDetails, setEditingDetails] = useState(false);
  const [details, setDetails] = useState(BLANK_DETAILS);
  const [addingDoc, setAddingDoc] = useState(false);
  const [previewing, setPreviewing] = useState(null); // { url, filename } of the CV or document open in the preview
  const [booking, setBooking] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await api.get(`talent/candidates/${candidateId}`);
      setC(d); setNotes(d.notes || '');
    } catch (e) { setError(e.message || 'Could not load the candidate.'); }
  }, [candidateId]);
  useEffect(() => { setRejecting(false); setErasing(false); setReason(''); setError(''); setMoveNote(''); load(); }, [load]);

  const act = async (fn, okMsg) => {
    setBusy(true); setError('');
    try {
      const d = await fn();
      if (d && d.id) { setC(d); setNotes(d.notes || ''); }
      else await load();
      if (okMsg) toast(okMsg, 'success');
      onChanged?.();
    } catch (e) {
      setError(e.message || 'That did not work.');
    } finally { setBusy(false); }
  };

  const move = (stage) => act(
    () => api.post(`talent/candidates/${c.id}/stage`, { stage, note: moveNote || undefined }).then(d => { setMoveNote(''); return d; }),
    `Moved to ${STAGE_LABELS[stage]}`,
  );
  const reject = () => act(
    () => api.post(`talent/candidates/${c.id}/reject`, { reason, notify: notifyOnReject && !!c.email }).then(d => { setRejecting(false); setReason(''); return d; }),
    notifyOnReject && c.email ? `Candidate rejected and regret email sent to ${c.email}` : 'Candidate rejected',
  );
  const saveNotes = () => act(() => api.put(`talent/candidates/${c.id}`, { notes }).then(d => { setEditingNotes(false); return d; }), 'Notes saved');
  const startDetails = () => { setDetails(detailsOf(c)); setEditingDetails(true); };
  // A hiring manager may edit the record but never the salary; the API drops
  // the field for them anyway, so this only keeps the request honest.
  const saveDetails = () => act(
    () => api.put(`talent/candidates/${c.id}`, canSeeSalary ? details : { ...details, desired_salary: undefined }).then(d => { setEditingDetails(false); return d; }),
    'Details saved',
  );
  const addDoc = (r) => act(
    () => api.post(`talent/candidates/${c.id}/documents`, { title: r.filename, url: r.url, filename: r.filename }).then(() => { setAddingDoc(false); return null; }),
    'Document added',
  );
  const removeDoc = (doc) => act(() => api.delete(`talent/candidates/${c.id}/documents/${doc.id}`).then(() => null), 'Document removed');

  const idx = c ? stageIndex(c.stage) : -1;
  const next = idx >= 0 && idx < STAGES.length - 1 ? STAGES[idx + 1] : null;
  const prev = idx > 0 ? STAGES[idx - 1] : null;
  const active = c?.status === 'active';

  return (
    <>
    <aside data-tour="candidate-panel"
      className="fixed top-0 right-0 bottom-0 w-full sm:w-[380px] bg-white border-l border-gray-200 shadow-2xl z-40 overflow-y-auto animate-fade-in">
      <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-start justify-between gap-2">
        {c ? (
          <div className="flex items-center gap-2 min-w-0">
            <span className="w-8 h-8 rounded-full bg-gold-light text-navy text-[11px] font-bold flex items-center justify-center flex-shrink-0">{initials(c.first_name, c.last_name)}</span>
            <div className="min-w-0">
              <div className="text-sm font-bold text-navy truncate">{c.first_name} {c.last_name}</div>
              <div className="text-[10px] text-gray-500 truncate">{c.vacancy_reference} · {c.vacancy_title}</div>
            </div>
          </div>
        ) : <div className="text-xs text-gray-400">Loading…</div>}
        <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-lg leading-none" aria-label="Close panel">&times;</button>
      </div>

      {c && (
        <div className="px-4 py-3 space-y-3 text-xs">
          <div className="flex flex-wrap gap-1 items-center">
            <Badge cls={STAGE_BADGE[c.stage]}>{STAGE_LABELS[c.stage]}</Badge>
            {c.status !== 'active' && <Badge cls={c.status === 'rejected' ? 'bg-red-50 text-red-700' : 'bg-gray-100 text-gray-600'}>{c.status}</Badge>}
            <Badge cls={c.days_in_stage > STALLED_AFTER_DAYS && active ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}>
              {c.days_in_stage} day{c.days_in_stage === 1 ? '' : 's'} in stage
            </Badge>
            {c.anonymised ? <Badge cls="bg-gray-100 text-gray-600">Anonymised {formatDate(c.anonymised_at)}</Badge> : null}
            {!c.anonymised && c.status !== 'hired' && (
              <button
                onClick={() => act(() => api.put(`talent/candidates/${c.id}`, { consent_future_roles: !c.consent_future_roles }), c.consent_future_roles ? 'No longer kept on file' : 'Kept on file for future roles')}
                disabled={busy}
                title={c.consent_future_roles ? 'The candidate agreed to be kept on file; click to withdraw that' : 'Record that the candidate agreed to be kept on file for future roles'}
                className={`inline-block px-1.5 py-0.5 rounded text-[9px] font-bold ${c.consent_future_roles ? 'bg-green-50 text-green-700' : 'bg-white border border-gray-200 text-gray-500 hover:border-gold'}`}>
                {c.consent_future_roles ? 'Keep on file ✓' : 'Keep on file?'}
              </button>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="text-[10px] font-bold text-gray-500 uppercase">Details</div>
            {!c.anonymised && <button className="text-[10px] text-gold hover:underline" onClick={startDetails}>{editingDetails ? 'Editing' : 'Edit'}</button>}
          </div>

          <dl className="grid grid-cols-[90px_1fr] gap-x-2 gap-y-1 text-[11px]">
            {editingDetails ? (<>
              <dt className="text-gray-500 self-center">Name</dt>
              <dd className="flex gap-1">
                <input className={inputCls} value={details.first_name} onChange={e => setDetails(d => ({ ...d, first_name: e.target.value }))} placeholder="First name" autoFocus />
                <input className={inputCls} value={details.last_name} onChange={e => setDetails(d => ({ ...d, last_name: e.target.value }))} placeholder="Last name" />
              </dd>
              <dt className="text-gray-500 self-center">Email</dt>
              <dd><input type="email" className={inputCls} value={details.email} onChange={e => setDetails(d => ({ ...d, email: e.target.value }))} /></dd>
              <dt className="text-gray-500 self-center">Phone</dt>
              <dd><input className={inputCls} value={details.phone} onChange={e => setDetails(d => ({ ...d, phone: e.target.value }))} /></dd>
              <dt className="text-gray-500 self-center">Job title</dt>
              <dd><input className={inputCls} value={details.job_title} onChange={e => setDetails(d => ({ ...d, job_title: e.target.value }))} /></dd>
              {canSeeSalary && (<>
                <dt className="text-gray-500 self-center">Desired salary</dt>
                <dd><input type="number" min="0" step="500" className={inputCls} value={details.desired_salary} onChange={e => setDetails(d => ({ ...d, desired_salary: e.target.value }))} placeholder="£ per year" /></dd>
              </>)}
              <dt className="text-gray-500 self-center">Source</dt>
              <dd className="flex gap-1">
                <select className={inputCls} value={details.source} onChange={e => setDetails(d => ({ ...d, source: e.target.value }))}>
                  <option value="">Not recorded</option>
                  {SOURCES.map(x => <option key={x} value={x}>{SOURCE_LABELS[x]}</option>)}
                </select>
                <input className={inputCls} value={details.source_detail} onChange={e => setDetails(d => ({ ...d, source_detail: e.target.value }))} placeholder="Detail" />
              </dd>
              <dt />
              <dd className="flex gap-1 items-center">
                <button className={btnPrimary} disabled={busy || !details.first_name.trim() || !details.last_name.trim()} onClick={saveDetails}>Save</button>
                <button className={btnGhost} disabled={busy} onClick={() => setEditingDetails(false)}>Cancel</button>
                {(!details.first_name.trim() || !details.last_name.trim()) && <span className="text-[10px] text-gray-500">A first and last name are needed.</span>}
              </dd>
            </>) : (<>
              <dt className="text-gray-500">Email</dt><dd className="text-gray-800 break-all">{c.email ? <a className="text-gold hover:underline" href={`mailto:${c.email}`}>{c.email}</a> : '—'}</dd>
              <dt className="text-gray-500">Phone</dt><dd className="text-gray-800">{c.phone || '—'}</dd>
              <dt className="text-gray-500">Job title</dt><dd className="text-gray-800">{c.job_title || '—'}</dd>
              {canSeeSalary && (<>
                <dt className="text-gray-500">Desired salary</dt><dd className="text-gray-800">{c.desired_salary != null ? money(c.desired_salary) : '—'}</dd>
              </>)}
              <dt className="text-gray-500">Source</dt><dd className="text-gray-800">{SOURCE_LABELS[c.source] || c.source || '—'}{c.source_detail ? ` · ${c.source_detail}` : ''}</dd>
            </>)}
            <dt className="text-gray-500">Applied</dt><dd className="text-gray-800">{formatDate(c.created_at)}</dd>
            <dt className="text-gray-500">CV</dt>
            <dd>{c.cv_url ? <button type="button" className="text-gold hover:underline text-left break-all" onClick={() => setPreviewing({ url: c.cv_url, filename: c.cv_filename, title: `CV: ${c.first_name} ${c.last_name}` })}>{c.cv_filename || 'View CV'}</button> : <span className="text-gray-400">none uploaded</span>}</dd>
            {c.status === 'rejected' && (<><dt className="text-gray-500">Rejected</dt><dd className="text-gray-800">{formatDate(c.rejected_at)}: {c.rejection_reason}</dd></>)}
          </dl>

          {active && (
            <div className="border border-gray-200 rounded p-2 space-y-2 bg-gray-50">
              <input className={inputCls} value={moveNote} onChange={e => setMoveNote(e.target.value)} placeholder="Note to record with the move (optional)" />
              <div className="flex flex-wrap gap-1">
                {next !== 'accepted' && (
                  <button className={btnPrimary} disabled={busy || !next} onClick={() => next && move(next)}>
                    {next ? `Move to ${STAGE_LABELS[next]} →` : 'At final stage'}
                  </button>
                )}
                <button className={btnGhost} disabled={busy || !prev} onClick={() => prev && move(prev)}>← Back{prev ? ` to ${STAGE_LABELS[prev]}` : ''}</button>
                <button className={`${btnGhost} text-red-700`} disabled={busy} onClick={() => { setRejecting(r => !r); setReason(''); }}>Reject</button>
              </div>
              {c.stage === 'offer' && (
                <div className="text-[10px] text-gray-500">Accepted is reached by recording an accepted offer below, which creates the person and their onboarding record.</div>
              )}
              {rejecting && (
                <div className="space-y-1">
                  <div className="flex gap-1">
                    <input className={inputCls} value={reason} onChange={e => setReason(e.target.value)} placeholder="Reason for rejecting (kept internal, required)" autoFocus />
                    <button className="px-3 py-1.5 text-[11px] font-semibold rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-50" disabled={busy || !reason.trim()} onClick={reject}>Confirm</button>
                  </div>
                  <label className={`flex items-center gap-1 text-[10px] ${c.email ? 'text-gray-600' : 'text-gray-400'}`}>
                    <input type="checkbox" checked={notifyOnReject && !!c.email} disabled={!c.email} onChange={e => setNotifyOnReject(e.target.checked)} />
                    {c.email ? `Email the candidate the regret letter (${c.email})` : 'No email on file, so no regret email can be sent'}
                  </label>
                </div>
              )}
            </div>
          )}

          {error && <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1">{error}</div>}

          <OfferSection candidate={{ ...c, vacancy_band: vacancyBand }} canSeeSalary={canSeeSalary} canManage={canManage} navigate={navigate} act={act} busy={busy} />

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold text-gray-500 uppercase">Interviews</div>
              {active && !booking && <button data-tour="book-interview" className="text-[10px] text-gold hover:underline" onClick={() => setBooking(true)}>Book interview</button>}
            </div>
            {booking && (
              <div className="mb-2">
                <BookInterviewForm candidate={c} people={people}
                  onBooked={(payload) => act(() => api.post('talent/interviews', payload).then(() => { setBooking(false); return null; }), 'Interview booked and invites sent')}
                  onCancel={() => setBooking(false)} />
              </div>
            )}
            {c.interviews?.length ? (
              <ul className="space-y-1.5">
                {c.interviews.map(iv => (
                  <li key={iv.id} className={`border rounded px-2 py-1.5 text-[11px] ${iv.status === 'cancelled' ? 'border-gray-200 opacity-60' : 'border-gray-200'}`}>
                    <div className="flex items-start justify-between gap-2">
                      <div>
                        <span className="font-semibold text-navy">{INTERVIEW_TYPE_LABELS[iv.type] || iv.type}</span>
                        <span className="text-gray-500"> · {fmtWhen(iv.scheduled_at)} · {iv.duration_minutes} min{iv.location ? ` · ${iv.location}` : ''}</span>
                        {iv.status === 'cancelled' && <Badge cls="bg-red-50 text-red-700">Cancelled</Badge>}
                      </div>
                      {iv.status === 'scheduled' && (
                        <button className="text-[10px] text-gray-400 hover:text-red-600" disabled={busy}
                          onClick={() => { if (window.confirm('Cancel this interview? The panel\'s Actions are closed and nobody is emailed.')) act(() => api.post(`talent/interviews/${iv.id}/cancel`, {}).then(() => null), 'Interview cancelled'); }}>
                          Cancel
                        </button>
                      )}
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {iv.panel.map(p => (
                        <li key={p.id} className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <span className="text-gray-700">{p.person_name}</span>
                            {p.feedback_notes && <div className="text-gray-500 italic whitespace-pre-wrap">{p.feedback_notes}</div>}
                          </div>
                          {p.recommendation
                            ? <Badge cls={REC_BADGE[p.recommendation]}>{RECOMMENDATION_LABELS[p.recommendation]}</Badge>
                            : <span className="text-[9px] text-gray-400 whitespace-nowrap">{iv.status === 'cancelled' ? '' : 'awaiting feedback'}</span>}
                        </li>
                      ))}
                    </ul>
                  </li>
                ))}
              </ul>
            ) : !booking && <div className="text-[11px] text-gray-400">No interviews yet.</div>}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold text-gray-500 uppercase">Notes</div>
              {!editingNotes && <button className="text-[10px] text-gold hover:underline" onClick={() => setEditingNotes(true)}>Edit</button>}
            </div>
            {editingNotes ? (
              <div className="space-y-1">
                <textarea rows={4} className={inputCls} value={notes} onChange={e => setNotes(e.target.value)} />
                <div className="flex gap-1">
                  <button className={btnPrimary} disabled={busy} onClick={saveNotes}>Save</button>
                  <button className={btnGhost} onClick={() => { setEditingNotes(false); setNotes(c.notes || ''); }}>Cancel</button>
                </div>
              </div>
            ) : (
              <div className="text-[11px] text-gray-700 whitespace-pre-wrap">{c.notes || <span className="text-gray-400">No notes yet.</span>}</div>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="text-[10px] font-bold text-gray-500 uppercase">Documents</div>
              <button className="text-[10px] text-gold hover:underline" onClick={() => setAddingDoc(a => !a)}>{addingDoc ? 'Cancel' : 'Add'}</button>
            </div>
            {addingDoc && <div className="mb-2"><FileUpload onUpload={addDoc} resetAfterUpload label="Drag a file here or click to browse" /></div>}
            {c.documents?.length ? (
              <ul className="space-y-1">
                {c.documents.map(d => (
                  <li key={d.id} className="flex items-center justify-between gap-2 text-[11px] border border-gray-200 rounded px-2 py-1">
                    <button type="button" className="text-gold hover:underline truncate text-left" onClick={() => setPreviewing({ url: d.url, filename: d.filename, title: d.title || d.filename })}>{d.title || d.filename}</button>
                    <button className="text-gray-400 hover:text-red-600 text-xs" disabled={busy} onClick={() => removeDoc(d)} aria-label="Remove document">&times;</button>
                  </li>
                ))}
              </ul>
            ) : <div className="text-[11px] text-gray-400">No documents beyond the CV.</div>}
          </div>

          <div>
            <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">History</div>
            <ul className="space-y-1">
              {c.history.map(h => (
                <li key={h.id} className="text-[11px] text-gray-700 border-l-2 border-gray-200 pl-2">
                  <span className="text-gray-400">{formatDate(h.changed_at)}</span>{' '}
                  {h.from_stage ? `${STAGE_LABELS[h.from_stage]} → ${STAGE_LABELS[h.to_stage]}` : `Added at ${STAGE_LABELS[h.to_stage]}`}
                  <span className="text-gray-400"> · {h.changed_by_name}</span>
                  {h.note && <div className="text-gray-600 italic">{h.note}</div>}
                </li>
              ))}
            </ul>
          </div>

          {/* The same key the API asks, and never for a hire: their record is
              a person in People and Onboarding now, and the API refuses it. */}
          {canDelete && c.status !== 'hired' && (
            <div className="border-t border-gray-200 pt-3">
              <button data-tour="delete-candidate" className={`${btnGhost} text-red-700`} disabled={busy} onClick={() => setErasing(true)}>Delete permanently</button>
              <div className="text-[10px] text-gray-500 mt-1">Erases the candidate and everything held about them, for a GDPR request. Reject keeps the record.</div>
            </div>
          )}
        </div>
      )}
    </aside>
    {previewing && <FilePreviewModal {...previewing} onClose={() => setPreviewing(null)} />}
    {erasing && c && (
      <DeleteCandidateModal candidate={c} onClose={() => setErasing(false)}
        onDeleted={(name) => { setErasing(false); toast(`${name} has been erased`, 'success'); onClose?.(); onChanged?.(); }} />
    )}
    </>
  );
}

// ─── Pipeline board ──────────────────────────────────────────────────────────

// The lanes and drag-and-drop are the shared KanbanBoard; only the candidate
// card is drawn here.
const TALENT_LANES = STAGES.map(stage => ({ key: stage, label: STAGE_LABELS[stage] }));

function Board({ candidates, vacanciesById, selectedId, onSelect, onDrop }) {
  return (
    <div data-tour="board">
    <KanbanBoard layout="grid" className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2"
      lanes={TALENT_LANES} items={candidates} laneOf={c => c.stage}
      onMove={onDrop} onSelect={c => onSelect(c.id)} selectedKey={selectedId}
      renderCard={(c, { selected }) => {
        const v = vacanciesById[c.vacancy_id];
        const stalled = c.days_in_stage > STALLED_AFTER_DAYS;
        return (
          <div className={`${candidateCardTint(c)} border rounded-md p-2 hover:shadow-md transition ${selected ? '!border-gold ring-2 ring-gold/25' : 'hover:border-gold'}`}>
            <div className="text-xs font-semibold text-navy truncate">{c.first_name} {c.last_name}</div>
            {c.job_title && <div className="text-[10px] text-gray-700 truncate">{c.job_title}</div>}
            <div className="text-[10px] text-gray-500 truncate">{v?.title || c.vacancy_title}</div>
            <div className="flex items-center justify-between mt-1.5 text-[9px]">
              <span className={stalled ? 'text-amber-700 font-semibold' : 'text-gray-500'}>{stalled ? '⚠ ' : ''}{c.days_in_stage}d in stage</span>
              {(c.proceed_count || c.hold_count || c.reject_count)
                ? <span className="truncate ml-1" title="Panel recommendations: proceed / hold / reject">
                    <span className="text-green-700 font-semibold">{c.proceed_count}✓</span> <span className="text-amber-700 font-semibold">{c.hold_count}?</span> <span className="text-red-700 font-semibold">{c.reject_count}✗</span>
                  </span>
                : <span className="text-gray-400 truncate ml-1">{c.next_interview_at ? `Interview ${fmtWhen(c.next_interview_at)}` : (SOURCE_LABELS[c.source] || '')}</span>}
            </div>
            {c.offer_status && (
              <div className="mt-1"><Badge cls={OFFER_BADGE[c.offer_status] || 'bg-gray-100 text-gray-600'}>{OFFER_STATUS_LABELS[c.offer_status] || c.offer_status}</Badge></div>
            )}
          </div>
        );
      }} />
    </div>
  );
}

// ─── Vacancy workspace ───────────────────────────────────────────────────────

// ─── Reports ─────────────────────────────────────────────────────────────────
// Whole-pipeline figures for holders of talent-acquisition:manage (HR and
// admin by default), computed by the API over every
// vacancy, plus the retention position and a button to run the purge now.

function ReportsView({ onRanPurge }) {
  const { toast } = useToast();
  const [r, setR] = useState(null);
  const [error, setError] = useState('');
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get('talent/reports').then(setR).catch(e => setError(e.message || 'Could not load the reports.')), []);
  useEffect(() => { load(); }, [load]);

  const runPurge = async () => {
    if (!window.confirm(`Anonymise ${r.retention.due_now} unsuccessful candidate${r.retention.due_now === 1 ? '' : 's'} now? This removes their names, contact details, notes and documents and cannot be undone. It runs nightly anyway.`)) return;
    setRunning(true);
    try {
      const res = await api.post('talent/retention/run', {});
      toast(`${res.anonymised} candidate${res.anonymised === 1 ? '' : 's'} anonymised`, 'success');
      await load(); onRanPurge?.();
    } catch (e) { toast(e.message || 'Could not run the purge', 'error'); }
    finally { setRunning(false); }
  };

  if (error) return <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>;
  if (!r) return <div className="text-xs text-gray-400">Loading…</div>;
  const Tile = ({ label, value, sub }) => (
    <div className="bg-white border border-gray-200 rounded-lg px-3 py-2">
      <div className="text-xl font-bold text-navy leading-tight">{value ?? '—'}</div>
      <div className="text-[10px] text-gray-500">{label}</div>
      {sub && <div className="text-[9px] text-gray-400">{sub}</div>}
    </div>
  );
  const days = (v) => (v == null ? '—' : `${v} day${v === 1 ? '' : 's'}`);

  return (
    <div data-tour="reports" className="space-y-3">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
        <Tile label="Open vacancies" value={r.vacancies.open} sub={`${r.vacancies.awaiting_approval} awaiting approval · ${r.vacancies.filled} filled`} />
        <Tile label="Active candidates" value={r.candidates.active} sub={`${r.candidates.hired} hired · ${r.candidates.rejected} unsuccessful`} />
        <Tile label="Time to hire" value={days(r.time_to_hire.average_days)} sub={r.time_to_hire.hires ? `${r.time_to_hire.hires} hire${r.time_to_hire.hires === 1 ? '' : 's'}, approval to acceptance (${days(r.time_to_hire.shortest_days)} to ${days(r.time_to_hire.longest_days)})` : 'no hires yet'} />
        <Tile label="Offer acceptance" value={r.offers.acceptance_rate == null ? '—' : `${r.offers.acceptance_rate}%`} sub={`${r.offers.accepted} accepted · ${r.offers.declined} declined · ${r.offers.in_progress} in progress · ${r.offers.above_band} above band`} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">Time in each stage</div>
          <table className="w-full text-xs">
            <tbody>
              {r.stage_durations.map(s => (
                <tr key={s.stage} className="border-t border-gray-100">
                  <td className="py-1 text-gray-700">{STAGE_LABELS[s.stage]}</td>
                  <td className="py-1 text-right text-navy font-semibold">{s.moves ? days(s.average_days) : '—'}</td>
                  <td className="py-1 text-right text-gray-400 text-[10px]">{s.moves} move{s.moves === 1 ? '' : 's'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <div className="text-[10px] text-gray-400 mt-1">Average days a candidate spends in a stage before moving on. Active now: {STAGES.map(s => `${STAGE_LABELS[s]} ${r.pipeline[s]}`).join(' · ')}.</div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-[10px] font-bold text-gray-500 uppercase mb-2">Source of hire</div>
          <table className="w-full text-xs">
            <thead><tr className="text-[9px] uppercase text-gray-500 text-left"><th className="py-1">Source</th><th className="py-1 text-right">Applicants</th><th className="py-1 text-right">Hired</th></tr></thead>
            <tbody>
              {r.source_of_hire.length === 0 && <tr><td colSpan={3} className="py-1 text-gray-400">No candidates yet.</td></tr>}
              {r.source_of_hire.map(s => (
                <tr key={s.source} className="border-t border-gray-100">
                  <td className="py-1 text-gray-700">{SOURCE_LABELS[s.source] || s.source}</td>
                  <td className="py-1 text-right text-navy font-semibold">{s.applicants}</td>
                  <td className="py-1 text-right text-green-700 font-semibold">{s.hired}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-3 space-y-2">
          <div>
            <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Interview feedback</div>
            <div className="text-xs text-gray-700">{r.feedback.submitted} recommendation{r.feedback.submitted === 1 ? '' : 's'} submitted, on average {r.feedback.average_hours} hours after the interview.</div>
            {r.feedback.outstanding > 0 && <div className="text-xs text-amber-700 mt-0.5">{r.feedback.outstanding} still outstanding from interviews that have happened.</div>}
          </div>
          <div className="border-t border-gray-100 pt-2">
            <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Retention</div>
            <div className="text-xs text-gray-700">
              Unsuccessful candidates without consent are anonymised {r.retention.months} months after their vacancy closes, nightly.
              {' '}{r.retention.anonymised_total} anonymised so far; {r.retention.kept_with_consent} kept with consent.
            </div>
            {r.retention.due_now > 0
              ? <div className="mt-1 flex items-center gap-2 text-xs"><span className="text-amber-700 font-semibold">{r.retention.due_now} due at the next run.</span><button className={btnGhost} disabled={running} onClick={runPurge}>{running ? 'Running…' : 'Run now'}</button></div>
              : <div className="text-[10px] text-gray-400 mt-1">Nothing is waiting to be anonymised.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Candidates ─────────────────────────────────────────────────────
// Every candidate on one list, whatever their status, so the ones who have
// left the board can still be found and corrected. A row opens the same panel
// the board opens: nothing is edited here, because the panel already owns it.

const CANDIDATE_STATUS_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'rejected', label: 'Rejected' },
  { key: 'hired', label: 'Hired' },
  { key: 'all', label: 'All' },
];

const CANDIDATE_STATUS_BADGE = {
  active: 'bg-green-50 text-green-700',
  rejected: 'bg-red-50 text-red-700',
  hired: 'bg-blue-50 text-blue-700',
  withdrawn: 'bg-gray-100 text-gray-600',
};

function CandidatesView({ candidates, vacancies, selectedId, onSelectCandidate, canSeeSalary }) {
  const [status, setStatus] = useState('active');
  const [vacancyId, setVacancyId] = useState('all');
  const [search, setSearch] = useState('');

  const counts = useMemo(() => {
    const out = { all: candidates.length, active: 0, rejected: 0, hired: 0, withdrawn: 0 };
    for (const c of candidates) if (c.status in out) out[c.status] += 1;
    return out;
  }, [candidates]);

  const q = search.trim().toLowerCase();
  const rows = candidates
    .filter(c => status === 'all' || c.status === status)
    .filter(c => vacancyId === 'all' || c.vacancy_id === vacancyId)
    .filter(c => !q || `${c.first_name} ${c.last_name} ${c.email || ''} ${c.job_title || ''} ${c.vacancy_reference || ''}`.toLowerCase().includes(q))
    .sort((a, b) => `${a.last_name} ${a.first_name}`.localeCompare(`${b.last_name} ${b.first_name}`));

  return (
    <div>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <div className="flex gap-1 flex-wrap">
          {CANDIDATE_STATUS_FILTERS.map(f => (
            <button key={f.key} onClick={() => setStatus(f.key)}
              className={`px-2 py-0.5 rounded-full text-[10px] font-semibold border transition-colors ${status === f.key ? 'bg-navy text-white border-gold' : 'bg-white text-gray-500 border-gray-200 hover:border-gold'}`}>
              {f.label} ({counts[f.key] ?? 0})
            </button>
          ))}
        </div>
        <select className="border border-gray-200 rounded px-2 py-1 text-xs bg-white text-gray-800" value={vacancyId} onChange={e => setVacancyId(e.target.value)}>
          <option value="all">All vacancies</option>
          {vacancies.map(v => <option key={v.id} value={v.id}>{v.reference} · {v.title}</option>)}
        </select>
        <input className="border border-gray-200 rounded px-2 py-1 text-xs bg-white text-gray-800 flex-1 min-w-[160px]"
          value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email, job title or vacancy" />
      </div>

      <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 text-[9px] uppercase text-gray-500 text-left">
              <th className="px-3 py-2">Candidate</th><th className="px-3 py-2">Job title</th>{canSeeSalary && <th className="px-3 py-2">Desired salary</th>}<th className="px-3 py-2">Vacancy</th><th className="px-3 py-2">Stage</th><th className="px-3 py-2">Days</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Status</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr><td colSpan={canSeeSalary ? 8 : 7} className="px-3 py-3 text-gray-400">
                {candidates.length === 0 ? 'No candidates yet.' : 'Nobody matches that. Try another status, or clear the search.'}
              </td></tr>
            )}
            {rows.map(c => (
              <tr key={c.id} onClick={() => onSelectCandidate(c.id)}
                className={`border-t border-gray-100 hover:bg-amber-50 cursor-pointer ${selectedId === c.id ? 'bg-amber-50' : ''}`}>
                <td className="px-3 py-2 font-semibold text-navy">
                  {c.first_name} {c.last_name}
                  {c.email && <div className="text-[10px] font-normal text-gray-500">{c.email}</div>}
                </td>
                <td className="px-3 py-2 text-gray-600">{c.job_title || '—'}</td>
                {canSeeSalary && <td className="px-3 py-2 text-gray-600">{c.desired_salary != null ? money(c.desired_salary) : '—'}</td>}
                <td className="px-3 py-2 text-gray-600">{c.vacancy_reference} <span className="text-gray-400">{c.vacancy_title}</span></td>
                <td className="px-3 py-2"><Badge cls={STAGE_BADGE[c.stage]}>{STAGE_LABELS[c.stage]}</Badge></td>
                <td className={`px-3 py-2 ${c.days_in_stage > STALLED_AFTER_DAYS && c.status === 'active' ? 'text-amber-700 font-semibold' : 'text-gray-600'}`}>{c.days_in_stage}</td>
                <td className="px-3 py-2 text-gray-600">{SOURCE_LABELS[c.source] || '—'}{c.source_detail ? ` · ${c.source_detail}` : ''}</td>
                <td className="px-3 py-2">
                  <Badge cls={CANDIDATE_STATUS_BADGE[c.status] || 'bg-gray-100 text-gray-600'}>{c.status}</Badge>
                  {c.anonymised_at ? <div className="text-[9px] text-gray-400">anonymised</div> : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-[10px] text-gray-500 mt-1">
        {rows.length} candidate{rows.length === 1 ? '' : 's'} shown. Click a row to open the candidate and edit their details.
      </div>
    </div>
  );
}

// ─── Vacancy workspace ───────────────────────────────────────────────────────

// ─── Reopen a vacancy ────────────────────────────────────────────────────────
// For a closed or filled role that needs somebody (else) after all. The dialog
// works out before it is sent whether the request needs the approver: within
// the headcount already approved it opens straight away, above it it goes to
// the Hiring tab of Approvals. The button says which, so nobody presses
// "Reopen" and finds it still filled. The API applies the same rule.

function ReopenVacancyModal({ vacancy: v, onDone, onClose }) {
  const filled = v.filled_count || 0;
  const allFilled = filled >= v.headcount;
  const [additional, setAdditional] = useState(allFilled ? 1 : 0);
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const extra = Math.max(0, parseInt(additional, 10) || 0);
  const headcount = v.headcount + extra;
  const approved = v.approved_headcount ?? v.headcount;
  const places = headcount - filled;
  const needsApproval = headcount > approved;
  const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;
  const statusWord = VACANCY_STATUS_LABELS[v.status].toLowerCase();

  const submit = async () => {
    if (places < 1) { setError('Add at least one more person: every place is already filled.'); return; }
    if (!reason.trim()) { setError('Say why it is being reopened.'); return; }
    setBusy(true); setError('');
    try {
      const updated = await api.post(`talent/vacancies/${v.id}/reopen`, { additional: extra, reason: reason.trim() });
      onDone(updated);
    } catch (e) {
      setError(e.message || 'Could not reopen the vacancy. Nothing has changed.');
      setBusy(false);
    }
  };

  return (
    <Modal title={`Reopen ${v.reference} ${v.title}?`} onClose={onClose}
      footer={<>
        {error && <span className="mr-auto self-center text-[10px] font-semibold text-red-700">{error}</span>}
        <button className={btnGhost} disabled={busy} onClick={onClose}>Keep it {statusWord}</button>
        <button className={btnPrimary} disabled={busy || places < 1 || !reason.trim()} onClick={submit}>
          {busy ? 'Saving…' : needsApproval ? 'Send for approval' : 'Reopen vacancy'}
        </button>
      </>}>
      <div className="space-y-3 text-[11px]">
        <p className="text-gray-700">
          {VACANCY_STATUS_LABELS[v.status]}{v.closed_at ? ` on ${formatDate(v.closed_at)}` : ''}, with a headcount
          of {v.headcount} and {filled} filled.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-[160px_1fr] gap-3">
          <div>
            <label className={labelCls}>More people needed</label>
            <input type="number" min={allFilled ? 1 : 0} className={inputCls} value={additional}
              onChange={e => { setAdditional(e.target.value); if (error) setError(''); }} />
          </div>
          <div>
            <label className={labelCls}>Why is it reopening?</label>
            <input className={inputCls} value={reason} autoFocus placeholder="e.g. a second engineer for the new contract"
              onChange={e => { setReason(e.target.value); if (error) setError(''); }} />
          </div>
        </div>
        {places < 1 ? (
          <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-red-700">
            Every place is filled, so reopening needs at least one more person on the headcount.
          </div>
        ) : needsApproval ? (
          <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-amber-700">
            That takes the headcount to {headcount}, above the {approved} already approved, so it goes to the vacancy
            approver first, on the Hiring tab of Approvals. It stays {statusWord} until they approve it, and if they
            turn it down nothing changes.
          </div>
        ) : (
          <div className="rounded border border-green-200 bg-green-50 px-3 py-2 text-green-700">
            It opens straight away with {plural(places, 'place')} to fill, within the headcount already approved.
          </div>
        )}
        <p className="text-gray-500">Candidates who were rejected earlier stay rejected.</p>
      </div>
    </Modal>
  );
}

function VacancyWorkspace({ vacancies, candidates, selectedVacancyId, onSelectVacancy, onSelectCandidate, onEdit, onClose, onReopen, onAddCandidate, canSeeSalary, canManage, showClosed, setShowClosed }) {
  const list = vacancies.filter(v => showClosed || ['pending_approval', 'open'].includes(v.status));
  const v = vacancies.find(x => x.id === selectedVacancyId) || list[0] || null;
  const cs = v ? candidates.filter(c => c.vacancy_id === v.id) : [];
  const funnel = STAGES.map(s => cs.filter(c => c.status === 'active' && stageIndex(c.stage) >= stageIndex(s)).length);
  const recent = [...cs].sort((a, b) => (b.updated_at || '').localeCompare(a.updated_at || '')).slice(0, 5);

  return (
    <div data-tour="workspace" className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-3">
      <div>
        <label className="flex items-center gap-1 text-[10px] text-gray-500 mb-1">
          <input type="checkbox" checked={showClosed} onChange={e => setShowClosed(e.target.checked)} /> Show closed, filled and rejected
        </label>
        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {list.length === 0 && <div className="p-3 text-xs text-gray-500">No vacancies yet.</div>}
          {list.map(x => (
            <button key={x.id} onClick={() => onSelectVacancy(x.id)}
              className={`w-full text-left px-3 py-2 border-b border-gray-100 hover:bg-gray-50 ${v?.id === x.id ? 'bg-amber-50 border-l-2 border-l-gold' : ''}`}>
              <div className="text-xs font-semibold text-navy">{x.title}</div>
              <div className="flex justify-between text-[10px] text-gray-500">
                <span>{x.team_name || 'No team'} · {x.hiring_manager_name || 'no manager'}</span>
                <span>{x.active_candidates} active</span>
              </div>
              <div className="mt-1"><Badge cls={VACANCY_BADGE[x.status]}>{VACANCY_STATUS_LABELS[x.status]}</Badge></div>
            </button>
          ))}
        </div>
      </div>

      {v ? (
        <div className="space-y-3">
          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <div className="text-base font-bold text-navy">{v.title} <span className="text-[10px] font-semibold text-gray-400">{v.reference}</span></div>
                <div className="text-[11px] text-gray-500">
                  {v.team_name || 'No team'} · Hiring manager {v.hiring_manager_name || 'not set'} · Headcount {v.headcount}{v.filled_count ? ` (${v.filled_count} filled)` : ''}
                  {canSeeSalary && (v.salary_band_low != null || v.salary_band_high != null) ? ` · Band ${money(v.salary_band_low)} to ${money(v.salary_band_high)}` : ''}
                  {canSeeSalary && (v.day_rate_band_low != null || v.day_rate_band_high != null) ? ` · Day rate ${money(v.day_rate_band_low)} to ${money(v.day_rate_band_high)}` : ''}
                  {v.start_by ? ` · Fill by ${formatDate(v.start_by)}` : ''}
                </div>
              </div>
              <div className="flex gap-1">
                <button className={btnGhost} onClick={() => onEdit(v)}>Edit vacancy</button>
                {v.status === 'open' && <button className={btnPrimary} onClick={() => onAddCandidate(v.id)}>+ Add candidate</button>}
                {canManage && ['open', 'pending_approval'].includes(v.status) && !v.reopen_from_status && <button className={`${btnGhost} text-red-700`} onClick={() => onClose(v)}>Close</button>}
                {canManage && ['closed', 'filled'].includes(v.status) && <button data-tour="reopen-vacancy" className={btnPrimary} onClick={() => onReopen(v)}>Reopen</button>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1 mt-2">
              <Badge cls={VACANCY_BADGE[v.status]}>{VACANCY_STATUS_LABELS[v.status]}</Badge>
              {v.approval_time && v.status !== 'rejected' && <Badge cls="bg-green-50 text-green-700">Approved {formatDate(v.approval_time)} by {v.approver_name}</Badge>}
              {v.status === 'pending_approval' && !v.reopen_from_status && <Badge cls="bg-amber-50 text-amber-700">Waiting for a vacancy approver</Badge>}
              {v.reopen_from_status && <Badge cls="bg-amber-50 text-amber-700">Reopen to a headcount of {v.reopen_headcount} waiting for the vacancy approver</Badge>}
              {v.reopened_at && <Badge cls="bg-green-50 text-green-700">Reopened {formatDate(v.reopened_at)} by {v.reopened_by_name}{v.reopen_approver_name ? `, approved by ${v.reopen_approver_name}` : ''}</Badge>}
              {v.status === 'rejected' && <Badge cls="bg-red-50 text-red-700">Rejected: {v.rejection_reason}</Badge>}
              <Badge cls="bg-gray-100 text-gray-600">Raised {formatDate(v.created_at)} by {v.created_by_name}</Badge>
              {v.reason && <Badge cls="bg-gray-100 text-gray-600">{REASON_LABELS[v.reason] || v.reason}</Badge>}
              <Badge cls="bg-gray-100 text-gray-600">{CLEARANCE_LABELS[v.clearance_target] || 'SC'}</Badge>
              {(v.channels || []).map(c => <Badge key={c} cls="bg-blue-50 text-blue-700">{SOURCE_LABELS[c] || c}</Badge>)}
              {v.folder_url && <a className="text-[10px] text-gold hover:underline" href={v.folder_url} target="_blank" rel="noreferrer">Folder</a>}
            </div>
            {v.reopen_from_status && v.reopen_request_reason && <div className="text-[11px] text-gray-600 mt-2">{v.reopen_requested_by_name || 'Somebody'} asked to reopen it: {v.reopen_request_reason}</div>}
            {v.reopened_at && v.reopen_reason && <div className="text-[11px] text-gray-600 mt-2">Reopened because: {v.reopen_reason}</div>}
            {v.description && <div className="text-[11px] text-gray-700 mt-2 whitespace-pre-wrap">{v.description}</div>}
            {v.notes && <div className="text-[11px] text-gray-500 mt-1 whitespace-pre-wrap">{v.notes}</div>}
          </div>

          <div className="grid grid-cols-3 sm:grid-cols-6 gap-1">
            {STAGES.map((s, i) => (
              <div key={s} className={`bg-white border rounded p-2 text-center ${s === 'offer' && funnel[i] ? 'border-gold bg-amber-50' : 'border-gray-200'}`}>
                <div className="text-base font-bold text-navy">{funnel[i]}</div>
                <div className="text-[9px] text-gray-500">{STAGE_LABELS[s]}</div>
              </div>
            ))}
          </div>

          <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-gray-50 text-[9px] uppercase text-gray-500 text-left">
                  <th className="px-3 py-2">Candidate</th><th className="px-3 py-2">Job title</th>{canSeeSalary && <th className="px-3 py-2">Desired salary</th>}<th className="px-3 py-2">Stage</th><th className="px-3 py-2">Days</th><th className="px-3 py-2">Source</th><th className="px-3 py-2">Status</th>
                </tr>
              </thead>
              <tbody>
                {cs.length === 0 && <tr><td colSpan={canSeeSalary ? 7 : 6} className="px-3 py-3 text-gray-400">No candidates yet.</td></tr>}
                {[...cs].sort((a, b) => stageIndex(b.stage) - stageIndex(a.stage)).map(c => (
                  <tr key={c.id} onClick={() => onSelectCandidate(c.id)} className="border-t border-gray-100 hover:bg-amber-50 cursor-pointer">
                    <td className="px-3 py-2 font-semibold text-navy">{c.first_name} {c.last_name}</td>
                    <td className="px-3 py-2 text-gray-600">{c.job_title || '—'}</td>
                    {canSeeSalary && <td className="px-3 py-2 text-gray-600">{c.desired_salary != null ? money(c.desired_salary) : '—'}</td>}
                    <td className="px-3 py-2"><Badge cls={STAGE_BADGE[c.stage]}>{STAGE_LABELS[c.stage]}</Badge></td>
                    <td className={`px-3 py-2 ${c.days_in_stage > STALLED_AFTER_DAYS && c.status === 'active' ? 'text-amber-700 font-semibold' : 'text-gray-600'}`}>{c.days_in_stage}</td>
                    <td className="px-3 py-2 text-gray-600">{SOURCE_LABELS[c.source] || '—'}{c.source_detail ? ` · ${c.source_detail}` : ''}</td>
                    <td className="px-3 py-2">{c.status === 'active' ? <span className="text-gray-500">Active</span> : <Badge cls="bg-red-50 text-red-700">{c.status}</Badge>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg p-3">
            <div className="text-[10px] font-bold text-gray-500 uppercase mb-1">Recent activity</div>
            {recent.length === 0 ? <div className="text-[11px] text-gray-400">Nothing yet.</div> : recent.map(c => (
              <div key={c.id} className="text-[11px] text-gray-700 py-0.5 border-b border-dashed border-gray-100 last:border-0">
                <span className="text-gray-400">{formatDate(c.updated_at)}</span> {c.first_name} {c.last_name}: {c.status === 'active' ? `at ${STAGE_LABELS[c.stage]} for ${c.days_in_stage} day${c.days_in_stage === 1 ? '' : 's'}` : c.status}
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-xs text-gray-500">Select a vacancy, or raise one.</div>
      )}
    </div>
  );
}

// ─── Page ────────────────────────────────────────────────────────────────────

// Active candidates are always on the board. Accepted (hired) candidates sit in
// the Accepted column and have their own tick, on by default; the rejected and
// withdrawn ones only appear when "Include rejected" is ticked.
function onBoard(c, { showAccepted, showRejected }) {
  if (c.status === 'active') return true;
  if (c.status === 'hired') return showAccepted;
  return showRejected;
}


export default function TalentAcquisitionPage() {
  const { navigate } = useApp();
  const { toast } = useToast();
  // The same keys src/api/talent.js asks, so the page draws only what the API
  // will serve: salary fields for talent-acquisition:see-salary, and the
  // controls that run the module (closing a vacancy, offers, reports) for
  // talent-acquisition:manage. Both are HR and admin by default.
  const canManage = canAccess('talent-acquisition:manage');
  const canSeeSalary = canAccess('talent-acquisition:see-salary');
  // Permanently erasing a candidate (a GDPR request) is a third key,
  // talent-acquisition:delete: HR, admin and the Senior Management Team.
  const canDelete = canAccess('talent-acquisition:delete');

  const [vacancies, setVacancies] = useState([]);
  const [candidates, setCandidates] = useState([]);
  const [teams, setTeams] = useState([]);
  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const [view, setView] = useState('board');            // board | candidates | vacancies | reports
  const [vacancyFilter, setVacancyFilter] = useState('all');
  const [stalledOnly, setStalledOnly] = useState(false);
  const [showRejected, setShowRejected] = useState(false);
  const [showAccepted, setShowAccepted] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [selectedVacancyId, setSelectedVacancyId] = useState(null);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [vacancyModal, setVacancyModal] = useState(null);  // null | 'new' | vacancy
  const [candidateModal, setCandidateModal] = useState(null); // null | { vacancyId }
  const [reopening, setReopening] = useState(null);           // vacancy being reopened

  // The Candidates view is the record of everyone, so it needs the rejected,
  // withdrawn and hired rows whatever the board's own ticks say.
  const allStatuses = showRejected || view === 'candidates';
  const load = useCallback(async () => {
    try {
      const [vs, cs] = await Promise.all([
        api.get('talent/vacancies'),
        api.get('talent/candidates', allStatuses ? { include_rejected: '1' } : showAccepted ? { include_hired: '1' } : undefined),
      ]);
      setVacancies(Array.isArray(vs) ? vs : []);
      setCandidates(Array.isArray(cs) ? cs : []);
      setLoadError('');
    } catch (e) {
      setLoadError(e.message || 'Could not load Talent Acquisition.');
    } finally { setLoading(false); }
  }, [allStatuses, showAccepted]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('teams').then(t => setTeams(Array.isArray(t) ? t : [])).catch(() => {});
    api.get('people').then(p => setPeople(Array.isArray(p) ? p : [])).catch(() => {});
  }, []);

  const tour = useFeatureTour(TALENT_TOUR_ID, { enabled: !loading });

  const vacanciesById = useMemo(() => Object.fromEntries(vacancies.map(v => [v.id, v])), [vacancies]);
  const openVacancies = vacancies.filter(v => v.status === 'open');
  const boardCandidates = candidates.filter(c =>
    (vacancyFilter === 'all' || c.vacancy_id === vacancyFilter) &&
    (!stalledOnly || c.days_in_stage > STALLED_AFTER_DAYS));
  const activeOnBoard = boardCandidates.filter(c => c.status === 'active');
  const tiles = {
    open: vacancyFilter === 'all' ? openVacancies.length : (vacanciesById[vacancyFilter]?.status === 'open' ? 1 : 0),
    pending: vacancies.filter(v => v.status === 'pending_approval').length,
    active: activeOnBoard.filter(c => c.stage !== 'accepted').length,
    stalled: activeOnBoard.filter(c => c.days_in_stage > STALLED_AFTER_DAYS && c.stage !== 'accepted').length,
  };

  const saveVacancy = async (payload) => {
    if (vacancyModal && vacancyModal !== 'new') {
      await api.put(`talent/vacancies/${vacancyModal.id}`, payload);
      toast('Vacancy updated', 'success');
    } else {
      const v = await api.post('talent/vacancies', payload);
      toast(`${v.reference} sent for approval`, 'success');
      setView('vacancies'); setSelectedVacancyId(v.id);
    }
    setVacancyModal(null);
    await load();
  };

  const saveCandidate = async (form) => {
    const c = await api.post('talent/candidates', form);
    toast(`${c.first_name} ${c.last_name} added`, 'success');
    setCandidateModal(null);
    await load();
    setSelectedCandidateId(c.id);
  };

  const closeVacancy = async (v) => {
    if (!window.confirm(`Close ${v.reference} ${v.title}? Candidates stay on record but no more can be added.`)) return;
    try { await api.post(`talent/vacancies/${v.id}/close`, {}); toast('Vacancy closed', 'success'); await load(); }
    catch (e) { toast(e.message || 'Could not close the vacancy', 'error'); }
  };

  const reopened = async (v) => {
    setReopening(null);
    toast(v.status === 'open' ? `${v.reference} reopened` : `${v.reference} sent to the vacancy approver to reopen`, 'success');
    await load();
  };

  const dropOnStage = async (candidateId, stage) => {
    const c = candidates.find(x => x.id === candidateId);
    if (!c || c.stage === stage || c.status !== 'active') return;
    // Optimistic: move the card, then confirm with the server.
    setCandidates(cs => cs.map(x => x.id === candidateId ? { ...x, stage, days_in_stage: 0 } : x));
    try {
      await api.post(`talent/candidates/${candidateId}/stage`, { stage });
      toast(`${c.first_name} ${c.last_name} moved to ${STAGE_LABELS[stage]}`, 'success');
    } catch (e) {
      toast(e.message || 'Could not move the candidate', 'error');
    }
    await load();
  };

  return (
    <div className="fade-in">
      <div data-tour="page-header" className="flex flex-wrap items-center justify-between gap-2 mb-3">
        <div>
          <h2 className="text-base font-extrabold text-navy">Talent Acquisition</h2>
          <div className="text-[9px] text-gray-500">Vacancies and candidates, from approval to an accepted offer. Onboarding takes over from there.</div>
        </div>
        <div className="flex flex-wrap items-center gap-1">
          <div data-tour="view-toggle" className="flex rounded overflow-hidden border border-gray-200">
            <button onClick={() => setView('board')} className={`px-3 py-1.5 text-[11px] font-semibold ${view === 'board' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Board</button>
            <button onClick={() => setView('candidates')} className={`px-3 py-1.5 text-[11px] font-semibold ${view === 'candidates' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Candidates</button>
            <button onClick={() => setView('vacancies')} className={`px-3 py-1.5 text-[11px] font-semibold ${view === 'vacancies' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Vacancies</button>
            {canManage && (
              <button onClick={() => setView('reports')} className={`px-3 py-1.5 text-[11px] font-semibold ${view === 'reports' ? 'bg-navy text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>Reports</button>
            )}
          </div>
          <button data-tour="raise-vacancy" className={btnGhost} onClick={() => setVacancyModal('new')}>+ Raise vacancy</button>
          <button className={btnGold} onClick={() => setCandidateModal({ vacancyId: vacancyFilter !== 'all' ? vacancyFilter : selectedVacancyId })} disabled={!openVacancies.length}
            title={openVacancies.length ? '' : 'Candidates can be added once a vacancy is approved'}>+ Add candidate</button>
          <button onClick={tour.start} aria-label="Replay the guided tour of this page" title="Show me around"
            className="w-7 h-7 rounded-full border border-gray-200 bg-white text-gray-500 hover:text-navy text-xs font-bold">?</button>
        </div>
      </div>

      {loadError && <div className="mb-3 text-xs text-red-700 bg-red-50 border border-red-200 rounded px-3 py-2">{loadError}</div>}

      {view === 'board' && (
        <>
          <div data-tour="filters" className="flex flex-wrap items-center gap-2 mb-3">
            <select className="border border-gray-200 rounded px-2 py-1 text-xs bg-white text-gray-800" value={vacancyFilter} onChange={e => setVacancyFilter(e.target.value)}>
              <option value="all">All open vacancies ({openVacancies.length})</option>
              {openVacancies.map(v => <option key={v.id} value={v.id}>{v.reference} · {v.title}</option>)}
            </select>
            <label className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 bg-white">
              <input type="checkbox" checked={stalledOnly} onChange={e => setStalledOnly(e.target.checked)} /> Only stalled (over {STALLED_AFTER_DAYS} days in stage)
            </label>
            <label className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 bg-white">
              <input type="checkbox" checked={showAccepted} onChange={e => setShowAccepted(e.target.checked)} /> Include accepted
            </label>
            <label className="flex items-center gap-1 text-xs text-gray-600 border border-gray-200 rounded px-2 py-1 bg-white">
              <input type="checkbox" checked={showRejected} onChange={e => setShowRejected(e.target.checked)} /> Include rejected
            </label>
          </div>

          <div data-tour="tiles" className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
            {[
              ['Open vacancies', tiles.open],
              ['Awaiting approval', tiles.pending],
              ['Active candidates', tiles.active],
              ['Stalled over a week', tiles.stalled],
            ].map(([label, value]) => (
              <div key={label} className="bg-white border border-gray-200 rounded-lg px-3 py-2">
                <div className="text-xl font-bold text-navy leading-tight">{value}</div>
                <div className="text-[10px] text-gray-500">{label}</div>
              </div>
            ))}
          </div>

          {loading ? <div className="text-xs text-gray-400">Loading…</div> : (
            openVacancies.length === 0 && candidates.length === 0 ? (
              <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-xs text-gray-500">
                <div className="text-sm font-semibold text-navy mb-1">No vacancies yet</div>
                Raise a vacancy to start. Once it is approved you can add candidates and they appear here.
                <div className="mt-3"><button className={btnPrimary} onClick={() => setVacancyModal('new')}>+ Raise vacancy</button></div>
              </div>
            ) : (
              <Board candidates={boardCandidates.filter(c => onBoard(c, { showAccepted, showRejected }))} vacanciesById={vacanciesById}
                selectedId={selectedCandidateId} onSelect={setSelectedCandidateId} onDrop={dropOnStage} />
            )
          )}
        </>
      )}

      {view === 'candidates' && (
        <CandidatesView candidates={candidates} vacancies={vacancies}
          selectedId={selectedCandidateId} onSelectCandidate={setSelectedCandidateId}
          canSeeSalary={canSeeSalary} />
      )}

      {view === 'reports' && canManage && <ReportsView onRanPurge={load} />}

      {view === 'vacancies' && (
        <VacancyWorkspace vacancies={vacancies} candidates={candidates}
          selectedVacancyId={selectedVacancyId} onSelectVacancy={setSelectedVacancyId}
          onSelectCandidate={setSelectedCandidateId}
          onEdit={(v) => setVacancyModal(v)} onClose={closeVacancy} onReopen={setReopening}
          onAddCandidate={(id) => setCandidateModal({ vacancyId: id })}
          canSeeSalary={canSeeSalary} canManage={canManage} showClosed={showClosed} setShowClosed={setShowClosed} />
      )}

      {selectedCandidateId && (
        <CandidatePanel candidateId={selectedCandidateId} people={people}
          canSeeSalary={canSeeSalary} canManage={canManage} canDelete={canDelete} navigate={navigate}
          vacancyBand={(() => { const v = vacanciesById[candidates.find(c => c.id === selectedCandidateId)?.vacancy_id]; return v ? { low: v.salary_band_low, high: v.salary_band_high, dayLow: v.day_rate_band_low, dayHigh: v.day_rate_band_high } : null; })()}
          onClose={() => setSelectedCandidateId(null)} onChanged={load} />
      )}
      {vacancyModal && (
        <VacancyModal vacancy={vacancyModal === 'new' ? null : vacancyModal} teams={teams} people={people}
          canSeeSalary={canSeeSalary} onSave={saveVacancy} onClose={() => setVacancyModal(null)} />
      )}
      {reopening && <ReopenVacancyModal vacancy={reopening} onDone={reopened} onClose={() => setReopening(null)} />}
      {candidateModal && (
        <CandidateModal vacancies={vacancies} defaultVacancyId={candidateModal.vacancyId} canSeeSalary={canSeeSalary} onSave={saveCandidate} onClose={() => setCandidateModal(null)} />
      )}

      {tour.open && (
        <FeatureTour steps={talentTourSteps} context={{ canManage, canDelete }} onFinish={tour.finish} onSkip={tour.skip} />
      )}
    </div>
  );
}
